import { ActivatedRoute, Router } from '@angular/router';
import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgForm } from '@angular/forms';

import { Observable } from 'rxjs';

import {
  ThfDialogService,
  ThfDynamicFormField,
  ThfNotificationService,
  ThfPageAction,
  ThfStepperItem,
  ThfStepperStatus
} from '@totvs/thf-ui';

import * as util from './../../utils/util';

import { ThfJobSchedulerInternal } from './interfaces/thf-job-scheduler-internal.interface';
import { ThfPageJobSchedulerInternal } from './thf-page-job-scheduler-internal';
import { ThfPageJobSchedulerBaseComponent } from './thf-page-job-scheduler-base.component';
import { thfPageJobSchedulerLiteralsDefault } from './thf-page-job-scheduler-literals';
import { ThfPageJobSchedulerLookupService } from './thf-page-job-scheduler-lookup.service';
import { ThfPageJobSchedulerService } from './thf-page-job-scheduler.service';

/**
 * @docsExtends ThfPageJobSchedulerBaseComponent
 *
 * @example
 *
 * <example name="thf-page-job-scheduler-basic" title="Totvs Page Job Scheduler Basic">
 *  <file name="sample-thf-page-job-scheduler-basic/sample-thf-page-job-scheduler-basic.component.html"> </file>
 *  <file name="sample-thf-page-job-scheduler-basic/sample-thf-page-job-scheduler-basic.component.ts"> </file>
 * </example>
 *
 */
@Component({
  selector: 'thf-page-job-scheduler',
  templateUrl: './thf-page-job-scheduler.component.html',
  encapsulation: ViewEncapsulation.None,
  styles: [`
    thf-container .thf-container {
      overflow-y: unset;
    }
  `]
})
export class ThfPageJobSchedulerComponent extends ThfPageJobSchedulerBaseComponent implements OnInit {

  isEdit = false;
  literals = {
    ...thfPageJobSchedulerLiteralsDefault[util.thfLocaleDefault],
    ...thfPageJobSchedulerLiteralsDefault[util.browserLanguage()]
  };
  parameters: Array<ThfDynamicFormField> = [];
  saveOperation: Observable<any>;
  step: number = 1;

  private backPageAction: ThfPageAction = {
    label: this.literals.back,
    action: this.nextStepOperation.bind(this, 'back'),
    disabled: this.isDisabledBack.bind(this)
  };

  private concludePageActions: Array<ThfPageAction> = [
    {
      label: this.literals.conclude,
      action: this.confirmJobScheduler.bind(this)
    },
    { ...this.backPageAction }
  ];

  private nextPageActions: Array<ThfPageAction> = [
    {
      label: this.literals.next,
      action: this.nextStepOperation.bind(this, 'next'),
      disabled: this.isDisabledAdvance.bind(this)
    },
    { ...this.backPageAction }
  ];

  jobSchedulerActions: Array<ThfPageAction> = [
    ...this.nextPageActions
  ];

  readonly steps: Array<ThfStepperItem> = [
    { label: this.literals.scheduling },
    { label: this.literals.parameterization },
    { label: this.literals.conclude }
  ];

  @ViewChild('schedulerExecution', { static: true }) schedulerExecution: { form: NgForm };
  @ViewChild('schedulerParameters', { static: true }) schedulerParameters: { form: NgForm };

  constructor(
    public thfPageDynamicLookupService: ThfPageJobSchedulerLookupService,
    private activatedRoute: ActivatedRoute,
    private thfDialogService: ThfDialogService,
    private thfNotification: ThfNotificationService,
    private router: Router,
    thfPageJobSchedulerService: ThfPageJobSchedulerService) {
      super(thfPageJobSchedulerService);
    }

  get stepperOrientation(): 'horizontal' | 'vertical' {
    return window.innerWidth > 481 && window.innerWidth < 960 ? 'horizontal' : 'vertical';
  }

  ngOnInit() {
    const paramId = this.activatedRoute.snapshot.params['id'];

    this.isEdit = !!paramId;

    this.thfPageJobSchedulerService.configServiceApi({ endpoint: this.serviceApi });

    this.loadData(paramId);
  }

  changePageActionsBySteps(currentStep: number, nextStep: number) {
    const stepsLength = this.steps.length;

    if (nextStep === stepsLength) {
    this.jobSchedulerActions = [ ...this.concludePageActions ];

    } else if (currentStep === stepsLength && nextStep < currentStep) {

    this.jobSchedulerActions = [ ...this.nextPageActions ];
    }
  }

  nextStep(stepNumber: number) {

    if (stepNumber > 1 && this.schedulerExecution.form.invalid) {
      this.markAsDirtyInvalidControls(this.schedulerExecution.form.controls);
      return;
    }

    if (stepNumber > 2 && (this.schedulerParameters && this.schedulerParameters.form && this.schedulerParameters.form.invalid)) {
      this.markAsDirtyInvalidControls(this.schedulerParameters.form.controls);
      return;
    }

    this.changePageActionsBySteps(this.step, stepNumber);

    const steps = this.steps[this.step - 1];
    this.step = stepNumber;

    if (steps) {
      steps.status = ThfStepperStatus.Done;
    }
  }

  onChangeProcess(process: { processId: string, existAPI: boolean }) {
    if (process.existAPI && process.processId) {
      this.getParametersByProcess(process.processId);

      if (!this.isEdit) {
        this.model.executionParameter = {};
      }

      return;
    }

  }

  private confirmJobScheduler() {
    const paramId = this.activatedRoute.snapshot.params['id'];

    const confirmMessage = paramId ? this.literals.confirmUpdateMessage : this.literals.confirmSaveMessage;

    this.thfDialogService.confirm({
      title: this.literals.confirmation,
      message: confirmMessage,
      confirm: () => {
        const model = Object.assign({}, this.model);

        this.save(model, paramId);
      }
    });

  }

  private async emitSuccessMessage(msgSuccess: any, saveOperation: Observable<any>) {
    await saveOperation.toPromise();
    this.thfNotification.success(msgSuccess);
    this.resetJobSchedulerForm();
  }

  private getParametersByProcess(process: any) {

    this.thfPageJobSchedulerService.getParametersByProcess(process).subscribe(parameters => {
      this.parameters = parameters;
    });

  }

  private isDisabledAdvance(): boolean {
    return this.schedulerExecution ? this.schedulerExecution.form.invalid : false;
  }

  private isDisabledBack(): boolean {
    return this.step === 1;
  }

  private nextStepOperation(operation?: 'back' | 'next') {
    const stepNumber = operation === 'back' ? this.step - 1 : this.step + 1;

    this.nextStep(stepNumber);
  }

  private resetJobSchedulerForm() {
    this.schedulerExecution.form.reset();

    // radiogroup nÃ£o estava atribuindo novo valor, fica vermelho sem o timetout.
    setTimeout(() => {
      this.model = new ThfPageJobSchedulerInternal();

      this.step = 1;
      this.steps.forEach(step => {
        step.status = ThfStepperStatus.Default;
      });

      this.jobSchedulerActions = [...this.nextPageActions];
    });

  }

  private save(model: ThfJobSchedulerInternal, paramId) {
    const saveOperation = paramId
      ? this.thfPageJobSchedulerService.updateResource(paramId, model)
      : this.thfPageJobSchedulerService.createResource(model);

    const msgSuccess = paramId ? this.literals.saveNotificationSuccessUpdate : this.literals.saveNotificationSuccessSave;

    this.emitSuccessMessage(msgSuccess, saveOperation);
  }

}
